import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Form, FormGroup, Input } from 'reactstrap';
import { useGetTemplateByIdQuery } from '@api/survey-templates';
import { useGetHistoryQuery } from '@api/survey-templates-history';
import { useAppSelector } from '../../../reducers';
import NotAuthorised from '../../not-authorised';
import { NotFound } from '../../not-found/NotFound';
import { getCurrentUser } from '../../../selectors/user';
import { getCheckOptions } from '@utils/survey';
import { getUtcDateUnit } from '@utils/date';
import Dashboard, { DashboardSection } from '@g17eco/molecules/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import { SurveyQuestionCollapseForm } from '@features/survey-configuration/partials/SurveyQuestionForm';
import { HideField, SurveyReportingSettings } from '@features/survey-configuration/partials/SurveyReportingSettings';
import { SurveyUnitsAndCurrencyCollapse } from '@features/units-currency-settings';
import { TemplateDashboardHeader } from '@features/survey-templates/partials/TemplateDashboardHeader';
import { TemplateHistoryHeader } from './TemplateHistoryHeader';
import { useGetSelectedHistory } from './hooks/useGetSelectedHistory';
import { TemplateHistoryNav } from './TemplateHistoryNav';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { NoteInstructionCollapseContainer } from '../../../features/note-instructions/NoteInstructionContainer';
import { SURVEY } from '@constants/terminology';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { getRootConfig } from '@selectors/globalData';
import { InitiativePermissions } from '@services/permissions/InitiativePermissions';

export const TemplateHistoryConfiguration = () => {
  const { templateId = '' } = useParams<{ templateId: string }>();
  const historyQuery = useGetHistoryQuery(templateId, { skip: !templateId });
  const templateQuery = useGetTemplateByIdQuery(templateId, { skip: !templateId });
  const currentUser = useAppSelector(getCurrentUser);
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);
  const canSetNoteInstructions = FeaturePermissions.canSetNoteInstructions(rootConfig);
  const checkOptions = getCheckOptions(canAccessVerification);
  const selectedHistory = useGetSelectedHistory(historyQuery.data);

  const canManage = useMemo(() => {
    if (!currentUser || !selectedHistory) {
      return false;
    }
    return InitiativePermissions.canManageInitiative(currentUser, selectedHistory.initiativeId);
  }, [currentUser, selectedHistory]);

  const reportingForm = useMemo(() => {
    if (!selectedHistory) {
      return undefined;
    }
    const { snapshot } = selectedHistory;
    return {
      ...snapshot,
      month: getUtcDateUnit('month', snapshot.effectiveDate),
      year: getUtcDateUnit('year', snapshot.effectiveDate),
    };
  }, [selectedHistory]);

  if (historyQuery.isFetching || templateQuery.isFetching) {
    return <Loader />;
  }

  if (!selectedHistory || !templateQuery.data || !reportingForm) {
    return <NotFound />;
  }

  if (!canManage) {
    return <NotAuthorised />;
  }

  return (
    <Dashboard>
      <TemplateHistoryHeader />
      <DashboardSection
        icon='fal fa-cog'
        header={
          <TemplateDashboardHeader title={`${SURVEY.CAPITALIZED_SINGULAR} templates: ${templateQuery.data.name}`} />
        }
      >
        <TemplateHistoryNav />
        <LoadingPlaceholder className='mt-4' count={1} height={800} isLoading={false}>
          <Form className='survey-config-form'>
            <div className='mt-4'>
              <h5>{SURVEY.CAPITALIZED_SINGULAR} name</h5>
              <Input disabled placeholder={selectedHistory.snapshot.name} />
            </div>
            <SurveyReportingSettings
              isDisabled
              form={reportingForm}
              hideFields={[HideField.BlueprintVersion]}
            />
            <SurveyQuestionCollapseForm
              disabled
              isCheckboxDisabled
              checkOptions={checkOptions}
              form={selectedHistory.snapshot}
              updateForm={() => {}}
            />
            <FormGroup className='mt-4' disabled>
              <SurveyUnitsAndCurrencyCollapse
                isDisabled
                unitConfig={selectedHistory.snapshot.unitConfig}
                useInitiativeSettings={selectedHistory.snapshot.useInitiativeSettings}
                toggleUseInitiativeSettings={() => {}}
              />
            </FormGroup>

            <NoteInstructionCollapseContainer
              editorId={templateId}
              updateForm={() => {}}
              disabled={true}
              canSetNoteInstructions={canSetNoteInstructions}
              noteInstructionsEditorState={selectedHistory.snapshot.noteInstructionsEditorState}
              noteInstructions={selectedHistory.snapshot.noteInstructions}
            />
          </Form>
        </LoadingPlaceholder>
      </DashboardSection>
    </Dashboard>
  );
};
